/** @file Client→server id remap: names both sides created under different ids, applied deep to values. */
import type { CommandResult } from '@pose2d/commands/types';
import type { Id } from '@pose2d/format/types';

const deep = (value: unknown, map: ReadonlyMap<Id, Id>): unknown => {
  if (typeof value === 'string') return map.get(value) ?? value;
  if (Array.isArray(value)) return value.map((item: unknown) => deep(item, map));
  if (typeof value !== 'object' || value === null) return value;
  return Object.fromEntries(Object.entries(value).map(([key, item]) => [map.get(key) ?? key, deep(item, map)]));
};

/** Building and applying a clientId → serverId map (E8-R4). */
export const remapIds = {
  /** clientId → serverId for every name the optimistic apply and the server created under different ids. */
  between(local: Record<string, Id>, results: readonly CommandResult[]): Map<Id, Id> {
    const server = results.reduce<Record<string, Id>>((all, r) => ({ ...all, ...r.created }), {});
    const map = new Map<Id, Id>();
    for (const [name, id] of Object.entries(local)) {
      const to = server[name];
      if (to !== undefined && to !== id) map.set(id, to);
    }
    return map;
  },
  /** A copy of `value` with every id the map holds replaced, in values and object keys; `value` itself for an empty map. */
  apply<T>(value: T, map: ReadonlyMap<Id, Id>): T {
    return map.size === 0 ? value : (deep(value, map) as T);
  },
};
